import { FormattedMessage, useIntl, type IntlShape } from "react-intl";

import {
  DEPRECATED_TRANSACTION_CTA_MESSAGES,
  TRANSACTION_CTA_MESSAGES,
} from "common/dashboard/constants";
import { useFeatureFlag } from "common/feature_gating";
import {
  ESIGN_TRANSACTION_TYPE,
  IDENTIFY_TRANSACTION_TYPE,
  VERIFY_TRANSACTION_TYPE,
} from "constants/transaction";
import {
  PROOF_TRANSACTIONS,
  HIDE_ESIGN_FROM_NEW_TRANSACTION_DROPDOWN,
  HIDE_CLOSE_FROM_NEW_TRANSACTION_DROPDOWN,
  LENDER_ESIGN,
  HIDE_NOTARIZATION_FROM_NEW_TRANSACTION_DROPDOWN,
  IDENTIFY_TRANSACTIONS,
} from "constants/feature_gates";
import { Heading, Paragraph } from "common/core/typography";
import { EmptyStateTile, EmptyStateTileGrid } from "common/dashboard/empty_state";
import { useSignTransactionsEnabled } from "util/feature_detection";
import { useCreateBusinessTransaction } from "common/transactions/create/hooks/use-create-business-transaction";
import {
  SIMPLE_NOTARIZATION_CONFIG,
  NOTARIZATION_CONFIG,
  SIGN_CONFIG,
  VERIFY_CONFIG,
  IDENTIFY_CONFIG,
  REAL_ESTATE_SIGN_CONFIG,
  REAL_ESTATE_IDENTIFY_CONFIG,
  OTHER_CONFIG,
} from "common/transaction_creation/v3/config";
import { useCreateRealEstateTransaction } from "common/transactions/create/hooks/use-create-real-estate-transaction";

import Styles from "./index.module.scss";

type BusinesssGettingStartedProps = {
  trackTransactionStarted?: (input: { isBatch?: boolean }) => void;
  dotloopEligible: boolean;
  handleImportLoop: () => void;
  isNstNotary: boolean;
  showVerifyOption?: boolean;
  canCreateSimpleNotarization?: boolean;
};

function GettingStartedHeader() {
  return (
    <div className={Styles.header}>
      <Heading level="h2" textStyle="headingFour">
        <FormattedMessage
          id="ebd23ccb-4296-4718-9bf7-5459a988dd3c"
          defaultMessage="Send your first transaction"
        />
      </Heading>
      <Paragraph size="large">
        <FormattedMessage
          id="b4d62691-a25e-476c-90a0-adb83643deb0"
          defaultMessage="You'll be able to track your signer's progress here."
        />
      </Paragraph>
    </div>
  );
}

function StandardNotarizationTile(props: {
  onClick(): void;
  intl: IntlShape;
  signTransactionsEnabled: boolean;
  isRealPortal?: boolean;
}) {
  const { intl, signTransactionsEnabled, isRealPortal } = props;

  const messages = signTransactionsEnabled
    ? TRANSACTION_CTA_MESSAGES
    : DEPRECATED_TRANSACTION_CTA_MESSAGES;

  const text = intl.formatMessage(messages.notarizeText);
  let subText = intl.formatMessage(messages.notarizeSubText);

  if (isRealPortal) {
    subText = intl.formatMessage(TRANSACTION_CTA_MESSAGES.realNotarizeSubText);
  }

  return (
    <EmptyStateTile
      onClick={props.onClick}
      data-automation-id="start-a-standard-transaction"
      text={text}
      subText={subText}
      iconName="notarize-filled"
    />
  );
}

function CloseTile(props: {
  onClick(): void;
  intl: IntlShape;
  signTransactionsEnabled: boolean;
  isTitlePortal?: boolean;
}) {
  const { intl, signTransactionsEnabled, isTitlePortal } = props;
  const messages = signTransactionsEnabled
    ? TRANSACTION_CTA_MESSAGES
    : DEPRECATED_TRANSACTION_CTA_MESSAGES;

  const text = intl.formatMessage(messages.closeText);
  let subText = intl.formatMessage(messages.lenderCloseSubText);

  if (isTitlePortal) {
    subText = intl.formatMessage(messages.titleCloseSubText);
  }

  return (
    <EmptyStateTile
      onClick={props.onClick}
      data-automation-id="start-a-real-estate-closing"
      text={text}
      subText={subText}
      iconName="address"
    />
  );
}

function EsignatureTile(props: {
  onClick(): void;
  intl: IntlShape;
  signTransactionsEnabled: boolean;
}) {
  const { intl, signTransactionsEnabled } = props;

  const messages = signTransactionsEnabled
    ? TRANSACTION_CTA_MESSAGES
    : DEPRECATED_TRANSACTION_CTA_MESSAGES;

  const text = intl.formatMessage(messages.signText);
  const subText = intl.formatMessage(messages.signSubText);

  return (
    <EmptyStateTile
      onClick={props.onClick}
      data-automation-id="start-an-esign-transaction"
      text={text}
      subText={subText}
      iconName="sign"
    />
  );
}

function IdentifyTile(props: { onClick(): void; intl: IntlShape }) {
  const { intl } = props;

  return (
    <EmptyStateTile
      onClick={props.onClick}
      data-automation-id="start-an-identify-transaction"
      text={intl.formatMessage(TRANSACTION_CTA_MESSAGES.identifyText)}
      subText={intl.formatMessage(TRANSACTION_CTA_MESSAGES.identifySubText)}
      iconName="secure"
    />
  );
}

function VerifyTile(props: { onClick(): void; intl: IntlShape }) {
  const { intl } = props;

  return (
    <EmptyStateTile
      onClick={props.onClick}
      data-automation-id="start-a-verify-transaction"
      text={intl.formatMessage(TRANSACTION_CTA_MESSAGES.verifyText)}
      subText={intl.formatMessage(TRANSACTION_CTA_MESSAGES.verifySubText)}
      iconName="video-chat"
    />
  );
}

export function BusinessGettingStarted(props: BusinesssGettingStartedProps) {
  const intl = useIntl();
  const {
    dotloopEligible,
    handleImportLoop,
    isNstNotary,
    showVerifyOption,
    canCreateSimpleNotarization,
  } = props;

  const signTransactionsEnabled = useSignTransactionsEnabled();
  const isProofEnabled = useFeatureFlag(PROOF_TRANSACTIONS) && !isNstNotary;
  const showEsignOption = !useFeatureFlag(HIDE_ESIGN_FROM_NEW_TRANSACTION_DROPDOWN);
  const showNotarizationOption = !useFeatureFlag(HIDE_NOTARIZATION_FROM_NEW_TRANSACTION_DROPDOWN);
  const showIdentifyOption = useFeatureFlag(IDENTIFY_TRANSACTIONS) && isProofEnabled;
  const { createTransaction } = useCreateBusinessTransaction();

  return (
    <>
      <GettingStartedHeader />

      <EmptyStateTileGrid>
        {showNotarizationOption && (
          <StandardNotarizationTile
            intl={intl}
            signTransactionsEnabled={signTransactionsEnabled}
            onClick={() => {
              props.trackTransactionStarted?.({});
              createTransaction({
                configId: canCreateSimpleNotarization
                  ? SIMPLE_NOTARIZATION_CONFIG.id
                  : NOTARIZATION_CONFIG.id,
              });
            }}
          />
        )}

        {showEsignOption && (
          <EsignatureTile
            onClick={() =>
              createTransaction({
                transactionType: ESIGN_TRANSACTION_TYPE,
                configId: SIGN_CONFIG.id,
              })
            }
            intl={intl}
            signTransactionsEnabled={signTransactionsEnabled}
          />
        )}

        {showIdentifyOption && (
          <IdentifyTile
            onClick={() =>
              createTransaction({
                transactionType: IDENTIFY_TRANSACTION_TYPE,
                configId: IDENTIFY_CONFIG.id,
              })
            }
            intl={intl}
          />
        )}

        {showVerifyOption && (
          <VerifyTile
            onClick={() =>
              createTransaction({
                transactionType: VERIFY_TRANSACTION_TYPE,
                configId: VERIFY_CONFIG.id,
              })
            }
            intl={intl}
          />
        )}

        {dotloopEligible && (
          <EmptyStateTile
            data-automation-id="import-a-loop-button"
            onClick={handleImportLoop}
            text={
              <FormattedMessage
                id="73c641e6-1af7-49d0-85bf-2f9a65e26356"
                defaultMessage="Import a loop"
              />
            }
            iconName="arrow-down-square"
          />
        )}
      </EmptyStateTileGrid>
    </>
  );
}

export function RealGettingStarted({
  handleRealEstateOption,
  isTitlePortal,
}: {
  handleRealEstateOption(): void;
  isTitlePortal?: boolean;
}) {
  const intl = useIntl();
  const showCloseOption = !useFeatureFlag(HIDE_CLOSE_FROM_NEW_TRANSACTION_DROPDOWN);
  const signTransactionsEnabled = useSignTransactionsEnabled();
  const isProofEnabled = useFeatureFlag(PROOF_TRANSACTIONS);
  const lenderEsignEnabled = useFeatureFlag(LENDER_ESIGN);
  const showEsignOption = !useFeatureFlag(HIDE_ESIGN_FROM_NEW_TRANSACTION_DROPDOWN);
  const showNotarizationOption = !useFeatureFlag(HIDE_NOTARIZATION_FROM_NEW_TRANSACTION_DROPDOWN);
  const showIdentifyOption = useFeatureFlag(IDENTIFY_TRANSACTIONS) && isProofEnabled;

  const { createTransaction } = useCreateRealEstateTransaction();

  return (
    <>
      <GettingStartedHeader />

      <EmptyStateTileGrid>
        {showCloseOption && (
          <CloseTile
            intl={intl}
            signTransactionsEnabled={signTransactionsEnabled}
            onClick={handleRealEstateOption}
            isTitlePortal={isTitlePortal}
          />
        )}

        {showNotarizationOption && (
          <StandardNotarizationTile
            intl={intl}
            isRealPortal
            signTransactionsEnabled={signTransactionsEnabled}
            onClick={() => {
              createTransaction({
                configId: OTHER_CONFIG.id,
              });
            }}
          />
        )}

        {showEsignOption && (lenderEsignEnabled || isTitlePortal) && (
          <EsignatureTile
            onClick={() =>
              createTransaction({
                configId: REAL_ESTATE_SIGN_CONFIG.id,
                transactionType: ESIGN_TRANSACTION_TYPE,
              })
            }
            intl={intl}
            signTransactionsEnabled={signTransactionsEnabled}
          />
        )}

        {showIdentifyOption && (
          <IdentifyTile
            onClick={() =>
              createTransaction({
                configId: REAL_ESTATE_IDENTIFY_CONFIG.id,
                transactionType: IDENTIFY_TRANSACTION_TYPE,
              })
            }
            intl={intl}
          />
        )}
      </EmptyStateTileGrid>
    </>
  );
}
